import React, { useState } from "react";
import {
  CpuChipIcon,
  CogIcon,
  CloudIcon,
  ShieldCheckIcon,
  ArrowPathIcon,
  ChartBarIcon,
  ChevronDownIcon,
  CheckCircleIcon,
  XCircleIcon,
} from "@heroicons/react/24/solid";
import data from "../data";
import { Link } from "react-router-dom";
import CallToActionSection from "../components/CallToActionSection";

// Map icon strings to HeroIcons components
const iconMapping = {
  ShieldCheckIcon,
  ArrowPathIcon,
  CpuChipIcon,
  CogIcon,
  CloudIcon,
  ChartBarIcon,
};

const Software = () => {
  const [expandedFeature, setExpandedFeature] = useState(null);

  const toggleFeature = (index) => {
    setExpandedFeature(expandedFeature === index ? null : index);
  };

  return (
    <div className="w-full">
      {/* Introduction Section + Video */}
      <div className="container mx-auto px-6 md:px-12 lg:px-16 xl:px-24 pt-24 flex flex-col md:flex-row gap-12">
        {/* Left - Intro Text */}
        <div className="w-full md:w-1/2">
          <h2 className="text-4xl font-bold text-primary">
            Upgrade to WallPen OS 3.0
          </h2>
          <p className="text-lg text-gray-700 mt-6">
            Every WallPen machine comes with our standard software, designed to
            deliver precision printing. But if you want access to cutting-edge
            automation, AI-powered enhancements, and seamless wireless control,
            upgrading to <strong>WallPen OS 3.0</strong> unlocks next-level
            features designed for professionals.
          </p>
          <p className="text-lg text-gray-700 mt-4">
            Check out the video to see how wallPen OS 3.0 allows you to focus on
            creativity while it takes care of the details, making printing
            faster, smarter, and completely seamless. Contact us to learn more.
          </p>
          {/* Call to Action Buttons */}
          <div className="mt-6 flex gap-4">
            <Link
              to="/contact"
              className="bg-primary text-white px-8 py-4 text-xl rounded-lg shadow-md transition-transform duration-300 ease-in-out hover:font-bold "
            >
              Contact Us
            </Link>
          </div>
        </div>

        {/* Right - Video */}
        <div className="w-full md:w-1/2 flex justify-center">
          <div className="w-full max-w-xl aspect-video rounded-lg shadow-lg overflow-hidden">
            <iframe
              className="w-full h-full"
              src={`https://www.youtube.com/embed/${new URL(
                data.software.videoUrl
              ).searchParams.get("v")}`}
              title="WallPen OS 3.0 Overview"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          </div>
        </div>
      </div>

      {/* Why Upgrade Section - Card Container */}
      <div className="container mx-auto px-6 md:px-12 lg:px-16 xl:px-24 pt-20">
        <div className="bg-gray-100 p-12 rounded-lg shadow-lg text-center">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
            {data.software.whyUpgrade.map((benefit, index) => {
              const IconComponent = iconMapping[benefit.icon];
              return (
                <div
                  key={index}
                  className="p-6 border rounded-lg shadow-md bg-white flex flex-col items-center"
                >
                  {IconComponent && (
                    <IconComponent className="text-secondary w-12 h-12 mb-4" />
                  )}
                  <h3 className="text-xl font-semibold text-primary">
                    {benefit.title}
                  </h3>
                  <p className="text-gray-600 mt-2">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Comparison Section */}
      <div className="container mx-auto px-6 md:px-12 lg:px-16 xl:px-24 py-24">
        <h2 className="text-4xl font-bold text-primary text-center">
          Compare Standard vs. Pro Software
        </h2>
        <div className="mt-12 overflow-x-auto">
          <table className="table-auto w-full text-left border-collapse border border-gray-300">
            <thead>
              <tr className="bg-gray-100">
                <th className="px-4 py-2 border border-gray-300 w-2/3">
                  Feature
                </th>
                <th className="px-4 py-2 border border-gray-300 text-center w-1/6">
                  Standard
                </th>
                <th className="px-4 py-2 border border-gray-300 text-center w-1/6">
                  Pro
                </th>
              </tr>
            </thead>
            <tbody>
              {data.software.comparison.map((item, index) => (
                <React.Fragment key={index}>
                  <tr
                    className={`cursor-pointer ${
                      index % 2 === 0 ? "bg-white" : "bg-gray-50"
                    }`}
                    onClick={() => toggleFeature(index)}
                  >
                    <td className="px-4 py-2 border border-gray-300">
                      <div className="flex justify-between items-center">
                        {item.feature}
                        <ChevronDownIcon
                          className={`w-5 h-5 text-primary transition-transform ${
                            expandedFeature === index ? "rotate-180" : ""
                          }`}
                        />
                      </div>
                      {expandedFeature === index && (
                        <div
                          className="mt-2 text-gray-700 text-sm bg-gray-200 p-3 rounded-lg"
                          dangerouslySetInnerHTML={{ __html: item.description }}
                        />
                      )}
                    </td>
                    <td className="px-4 py-2 border border-gray-300 text-center">
                      {item.standard ? (
                        <CheckCircleIcon className="w-6 h-6 text-green-500 mx-auto" />
                      ) : (
                        <XCircleIcon className="w-6 h-6 text-red-500 mx-auto" />
                      )}
                    </td>
                    <td className="px-4 py-2 border border-gray-300 text-center">
                      {item.pro ? (
                        <CheckCircleIcon className="w-6 h-6 text-green-500 mx-auto" />
                      ) : (
                        <XCircleIcon className="w-6 h-6 text-red-500 mx-auto" />
                      )}
                    </td>
                  </tr>
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <CallToActionSection
        title="Ready to Upgrade to WallPen OS 3.0?"
        description="Unlock advanced features and transform your vertical printing business."
        primaryAction={{
          text: "Contact Us",
          link: "/contact?type=Upgrade%20Inquiry",
        }}
      />
    </div>
  );
};

export default Software;
